import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { ResponsiveSVG } from 'ui/acro'
import InfoIcon from '../../../../assets/images/info.svg'

const Wrapper = styled.div`
  position: relative;
  display: inline-block;

  @media (hover: hover) and (pointer: fine) {
    &:hover > div {
      visibility: visible;
      opacity: 1;
    }
  }
`

const InfoIconWrapper = styled.span`
  cursor: pointer;
  font-size: 20px;
`

const Tooltip = styled.div`
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  position: absolute;
  bottom: 100%; /* Position above the icon */
  /* left and transform will be overridden inline */
  margin-bottom: 0px;
  padding: 12px 16px;
  background-color: #000;
  color: #fff;
  border-radius: 4px;
  white-space: normal;
  width: 200px;
  word-wrap: break-word;
  z-index: 1000;
  font-size: 14px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  font-family: 'Proxima-Nova';
  line-height: 18px;
  z-index: 1003;
  @media (min-width: 500px) {
    left: 50%;
    transform: translateX(-50%);
  }
`

const TooltipArrow = styled.div`
  position: absolute;
  top: 100%; /* arrow below tooltip */
  /* left and margin-left overridden inline */
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #333;
  @media (min-width: 500px) {
    left: 50%;
    margin-left: -6px;
  }
`

const InfoTooltip = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [canHover, setCanHover] = useState(false)

  const handleMouseEnter = () => {
    if (canHover) setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    if (canHover) setShowTooltip(false)
  }

  // Tooltip positioning state
  const [tooltipStyles, setTooltipStyles] = useState({
    left: '50%',
    transform: 'translateX(-50%)'
  })
  const [arrowStyles, setArrowStyles] = useState({
    left: '50%',
    marginLeft: '-6px'
  })
  const wrapperRef = useRef(null)
  const tooltipRef = useRef(null)

  // Detect if device supports hover

  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)')
    setCanHover(mediaQuery.matches)
    const handler = (e) => setCanHover(e.matches)
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  // Close tooltip on click outside (only for touch devices)
  useEffect(() => {
    if (showTooltip) {
      const handleClickOutside = (event) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
          setShowTooltip(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside)
      }
    }
  }, [showTooltip, canHover])

  useEffect(() => {
    if (!showTooltip || !tooltipRef.current || !wrapperRef.current) {
      return
    }

    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const wrapperRect = wrapperRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const margin = 8 // margin from edge

    // Calculate center position of icon relative to viewport
    const iconCenterX = wrapperRect.left + wrapperRect.width / 2

    // Calculate where tooltip's left edge would be if centered
    const tooltipLeftEdge = iconCenterX - tooltipRect.width / 2
    const tooltipRightEdge = iconCenterX + tooltipRect.width / 2

    let left = '50%'
    let transform = 'translateX(-50%)'
    let arrowLeft = '50%'
    let arrowMarginLeft = '-6px'
    if (tooltipLeftEdge < margin) {
      // Tooltip would overflow left side
      left = `${0}px`
      transform = 'translateX(0)'
      // arrowLeft: relative to tooltip box, arrow needs to align with icon center
      const arrowPos = iconCenterX - wrapperRect.left
      arrowLeft = `${arrowPos}px`
      arrowMarginLeft = '-6px'
    } else if (tooltipRightEdge > viewportWidth - 16) {
      // Tooltip would overflow right side
      left = `-${tooltipRect.width - 16}px`
      transform = 'translateX(0)'
      // arrowLeft relative to tooltip
      const arrowPos = tooltipRect.width - margin
      arrowLeft = `${arrowPos}px`
      arrowMarginLeft = '-6px'
    }

    setTooltipStyles({ left, transform })
    setArrowStyles({ left: arrowLeft, marginLeft: arrowMarginLeft })
  }, [showTooltip])

  return (
    <Wrapper
      ref={wrapperRef}
      onClick={() => {
        setShowTooltip((prev) => !prev)
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <InfoIconWrapper
        aria-label='Info'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setShowTooltip((prev) => !prev)
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ResponsiveSVG
          SVG={InfoIcon}
          width='20px'
          height='20px'
          style={{ verticalAlign: 'middle' }}
        />
      </InfoIconWrapper>

      {showTooltip && (
        <Tooltip
          ref={tooltipRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            visibility: showTooltip ? 'visible' : 'hidden',
            opacity: showTooltip ? 1 : 0,
            pointerEvents: showTooltip ? 'auto' : 'none',
            left: tooltipStyles.left,
            transform: tooltipStyles.transform
          }}
        >
          {text}
          <TooltipArrow
            style={{
              left: arrowStyles.left,
              marginLeft: arrowStyles.marginLeft
            }}
          />
        </Tooltip>
      )}
    </Wrapper>
  )
}

export default InfoTooltip
